import { useSyncExternalStore } from 'react';
import type { PullResponse, PushResponse } from '@dairy/shared';
import { db } from '@/db/db';
import { onSaved } from '@/db/repo';
import { deviceId, hasAccessToken, refresh, sessionStore, updateProfile } from '@/auth/session';
import { API_BASE, apiFetch, ApiError } from '@/lib/api';
import { SAVE_DEBOUNCE_MS, SYNC_INTERVAL_MS, SyncEngine, type SyncStatus, type SyncTransport } from './engine';
import { dailyReconcile, reconcileAllReminders } from '@/features/events/effects';

async function call<T>(fn: () => Promise<Response>): Promise<{ status: number; body?: T }> {
  try {
    const res = await fn();
    return { status: res.status, body: (await res.json()) as T };
  } catch (e) {
    if (e instanceof ApiError) {
      if (e.code === 'NETWORK') throw e;
      return { status: e.status };
    }
    throw e;
  }
}

const transport: SyncTransport = {
  async push(body) {
    if (!hasAccessToken() && !(await refresh())) return { status: navigator.onLine ? 401 : 0 };
    return call<PushResponse>(() => apiFetch('/sync/push', { method: 'POST', json: body }));
  },
  async pull(cursor, limit) {
    if (!hasAccessToken() && !(await refresh())) return { status: navigator.onLine ? 401 : 0 };
    return call<PullResponse>(() => apiFetch(`/sync/pull?cursor=${cursor}&limit=${limit}`));
  },
  async uploadFile(id, blob, mimeType) {
    const res = await apiFetch(`/files/${id}`, { method: 'PUT', body: blob, headers: { 'Content-Type': mimeType } });
    return res.status;
  },
  refresh,
  deviceId,
};

export const syncEngine = new SyncEngine(db, transport, {
  capturePullSession: () => {
    const session = sessionStore.get();
    return () => session.status === 'signedIn' && sessionStore.get().status === 'signedIn' && sessionStore.get().profile === session.profile;
  },
  onTenant: async (tenant) => {
    await updateProfile({ tenantName: tenant.name, settings: tenant.settings });
  },
  onFarmRulesChanged: async () => {
    await reconcileAllReminders();
  },
  afterSync: async () => {
    await dailyReconcile();
  },
});

let started = false;
let interval: ReturnType<typeof setInterval> | null = null;
let debounce: ReturnType<typeof setTimeout> | null = null;
let offSaved: (() => void) | null = null;

const onOnline = () => void syncEngine.run();

/** Triggers (S1-T12): app start, `online`, every 60 s while online, 2 s after a save, and "Sync now". */
export function startSync(): void {
  if (started) return;
  started = true;
  void syncEngine.refreshCounts();
  void syncEngine.run();
  window.addEventListener('online', onOnline);
  interval = setInterval(() => {
    if (navigator.onLine) void syncEngine.run();
  }, SYNC_INTERVAL_MS);
  offSaved = onSaved(() => {
    void syncEngine.refreshCounts();
    if (debounce) clearTimeout(debounce);
    debounce = setTimeout(() => void syncEngine.run(), SAVE_DEBOUNCE_MS);
  });
}

export function stopSync(): void {
  started = false;
  window.removeEventListener('online', onOnline);
  if (interval) clearInterval(interval);
  if (debounce) clearTimeout(debounce);
  offSaved?.();
  syncEngine.stop();
}

export function syncNow(): Promise<unknown> {
  return syncEngine.run();
}

export function useSyncStatus(): SyncStatus {
  return useSyncExternalStore(
    (fn) => syncEngine.subscribe(fn),
    () => syncEngine.getStatus(),
  );
}

export { API_BASE };
